import { getNombreProveedor } from "./proveedores";
import { client } from "./supabase";

const ONSIGN_PRECIO_POR_PANTALLA = 10.99;

const contarPantallasActivas = async (pais, fecha) => {
  const [edificiosRes, carteleriaRes] = await Promise.all([
    client
      .from("edificios")
      .select("pantallas, cowork, espera, totems, inicio_servicio")
      .eq("pais", pais)
      .eq("estado", "OPERATIVO")
      .lte("inicio_servicio", fecha),
    client
      .from("negocios_carteleria")
      .select("cantidad_pantallas, fecha_inicio, plazo_contrato_meses")
      .eq("pais", pais)
      .eq("eliminada", false)
      .or("estado.eq.Activo,estado.eq.activo,estado.eq.100%")
      .lte("fecha_inicio", fecha),
  ]);

  let total = 0;

  for (const e of edificiosRes.data || []) {
    total += (Number(e.pantallas) || 0) + (Number(e.cowork) || 0) + (Number(e.espera) || 0) + (Number(e.totems) || 0);
  }

  for (const n of carteleriaRes.data || []) {
    if (!n.cantidad_pantallas) continue;
    if (n.plazo_contrato_meses && n.fecha_inicio) {
      const fechaFin = new Date(n.fecha_inicio);
      fechaFin.setMonth(fechaFin.getMonth() + parseInt(n.plazo_contrato_meses, 10));
      const fechaFinStr = fechaFin.toISOString().split("T")[0];
      if (fechaFinStr < fecha) continue;
    }
    total += Number(n.cantidad_pantallas);
  }

  return total;
};

export const getProveedoresRecurrentesEntries = async (
  pais,
  monedaLocal = "CLP",
  facturasRecibidas = [],
  facturasExternas = [],
  // Regla 10.163 (solo Chile): movimientos_caja para conciliar los recurrentes por pago real.
  movimientosCaja = [],
  // Bloque C: boletas de honorarios del país (crudas). Un proveedor de honorarios mensual se
  // deduplica contra su boleta real del mes (si no, la proyección recurrente la duplicaría).
  boletasHonorarios = []
) => {
  const { data: proveedores, error } = await client
    .from("proveedores")
    .select(
      "id, nombre, alias, concepto_flujo, monto_recurrente, moneda_recurrente, moneda_default, dia_pago, periodicidad, mes_sin_cobro, recurrente_desde, recurrente_hasta, recurrente_con_contrato, cuotas_adelantadas"
    )
    .eq("pais", pais)
    .eq("habilitado", true)
    .eq("es_recurrente", true);

  if (error) throw error;

  // Costos asociados (Fase 2): filas de proveedor_costos_asociados de los proveedores CON CONTRATO.
  // Cada una proyecta su propia serie recurrente más abajo. Los proveedores abiertos no tienen
  // costos → no cambia nada para ellos (backward-compatible).
  const provConContrato = (proveedores || []).filter((p) => p.recurrente_con_contrato);
  let costosAsociados = [];
  if (provConContrato.length > 0) {
    const { data: costosData, error: errCostos } = await client
      .from("proveedor_costos_asociados")
      .select("id, proveedor_id, concepto, monto, moneda, dia_pago, tipo, fecha")
      .in(
        "proveedor_id",
        provConContrato.map((p) => p.id)
      );
    if (errCostos) throw errCostos;
    costosAsociados = costosData || [];
  }
  const provPorId = new Map((proveedores || []).map((p) => [p.id, p]));

  // Overrides de fecha por OCURRENCIA (pago_recurrente_fecha). Batch por país + proveedores
  // recurrentes en juego (patrón getFechasPagoPais de sueldos, sin N+1). La tabla tiene columna
  // `pais` propia → filtro directo, sin !inner. Indexado por la clave de la ocurrencia CON su
  // discriminador `costo_asociado_id`: `${proveedor_id}-${periodo_año}-${periodo_mes}-${costo_asociado_id ?? "main"}`
  // (mes 1-based, sin padding, para matchear los ints de la DB). `main` = línea principal
  // (costo_asociado_id NULL); un id = ese costo asociado mensual. Sin fila → la ocurrencia usa la
  // fecha derivada de dia_pago (comportamiento previo). Tabla vacía → Map vacío y NADA cambia.
  const overridesFechaPorOcurrencia = new Map();
  const idsProvsRecurrentes = (proveedores || []).map((p) => p.id);
  if (idsProvsRecurrentes.length > 0) {
    const { data: overridesData, error: errOverrides } = await client
      .from("pago_recurrente_fecha")
      .select("proveedor_id, periodo_año, periodo_mes, fecha_override, costo_asociado_id")
      .eq("pais", pais)
      .in("proveedor_id", idsProvsRecurrentes);
    if (errOverrides) throw errOverrides; // 10.177: no tragarse el error a [] — enmascara fallas.
    for (const o of overridesData || []) {
      if (!o.proveedor_id || !o.periodo_año || !o.periodo_mes || !o.fecha_override) continue;
      const disc = o.costo_asociado_id ?? "main";
      overridesFechaPorOcurrencia.set(`${o.proveedor_id}-${o.periodo_año}-${o.periodo_mes}-${disc}`, o.fecha_override);
    }
  }

  // Suma `n` meses a una fecha YYYY-MM-DD y devuelve el mes resultante como "YYYY-MM" (aritmética
  // pura sobre año/mes, sin timezone). Usada para correr el inicio de la línea principal por las
  // cuotas ya pagadas por adelantado.
  const shiftMesYYYYMM = (fechaStr, n) => {
    const [y, m] = fechaStr.slice(0, 7).split("-").map(Number); // m es 1-based
    const idx = y * 12 + (m - 1) + n;
    return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
  };

  // Set de `${proveedor_id}-YYYY-MM` que ya tienen factura (SII o externa) en ese mes calendario.
  // Si existe, no generamos entry sintética para evitar doble conteo con la fila de "Por pagar".
  // Comparación SIEMPRE por proveedor_id numérico.
  const mesProveedorConFactura = new Set();
  for (const f of facturasRecibidas) {
    if (!f.proveedor_id || !f.fecha_emision) continue;
    mesProveedorConFactura.add(`${f.proveedor_id}-${f.fecha_emision.slice(0, 7)}`);
  }
  // Externas: preferir periodo_mes (mes facturado, semánticamente correcto) y caer a
  // fecha_emision cuando no está poblado. Esto cubre proveedores extranjeros (OnSign,
  // etc.) que no tienen DTE SII y por lo tanto nunca aparecen en facturasRecibidas.
  for (const f of facturasExternas) {
    if (!f.proveedor_id) continue;
    const mesRef = f.periodo_mes ? f.periodo_mes.slice(0, 7) : f.fecha_emision ? f.fecha_emision.slice(0, 7) : null;
    if (!mesRef) continue;
    mesProveedorConFactura.add(`${f.proveedor_id}-${mesRef}`);
  }
  // Boletas de honorarios (Bloque C): solo las que computan cuentan como documento real del mes.
  for (const b of boletasHonorarios) {
    if (!b.computa || !b.proveedor_id || !b.fecha_emision) continue;
    mesProveedorConFactura.add(`${b.proveedor_id}-${b.fecha_emision.slice(0, 7)}`);
  }

  // P6 (Fase 1) — El monto de la proyección de recurrentes GENÉRICOS (no OnSign) es el PROMEDIO de los
  // meses con factura dentro de la ventana de los 3 meses calendario más recientes CONTANDO el mes en
  // curso (ej: julio → {mayo, junio, julio}). Se suman facturas_sii_recibidas + facturas_externas del
  // proveedor en su moneda operativa (moneda_default); un mes con 2 folios suma ambos. El divisor es la
  // cantidad de meses PRESENTES (con al menos una factura), NO 3 fijo: así un proveedor mensual estable
  // proyecta su mensualidad aunque la factura del mes en curso todavía no esté cargada. Fallback a
  // monto_recurrente si no hay ninguna factura en la ventana. Comparación por proveedor_id numérico
  // (regla 10.7). NOTA: query DEDICADO — no se reutilizan los arrays facturasRecibidas/facturasExternas
  // del parámetro (llegan filtrados por estado: getFacturasRecibidas usa estado_not:"ignorada", sirven
  // para otros usos). facturas_sii_recibidas NO tiene columna moneda: sus montos están siempre en moneda
  // local, por eso solo se suman cuando moneda_default === monedaLocal.
  const provIds = (proveedores || []).map((p) => p.id);
  const monedaDefaultPorProv = new Map(
    (proveedores || []).map((p) => [p.id, p.moneda_default || p.moneda_recurrente || monedaLocal])
  );

  // Ventana: los 3 meses calendario más recientes contando el mes en curso (como strings YYYY-MM,
  // construidos desde getFullYear/getMonth para NO pasar por toISOString y evitar corrimientos de TZ).
  const hoyBase = new Date();
  const ventanaMeses = [];
  for (let i = 0; i < 3; i += 1) {
    const d = new Date(hoyBase.getFullYear(), hoyBase.getMonth() - i, 1);
    ventanaMeses.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  // Piso del query: primer día del mes (hoy − 3), un mes de margen sobre la ventana para no perder
  // externas cuyo periodo_mes cae en la ventana pero con fecha_emision algo anterior.
  const pisoD = new Date(hoyBase.getFullYear(), hoyBase.getMonth() - 3, 1);
  const desdeFecha = `${pisoD.getFullYear()}-${String(pisoD.getMonth() + 1).padStart(2, "0")}-01`;

  // key `${proveedor_id}-YYYY-MM` → suma de montos en moneda operativa. La existencia de la key marca
  // el mes como "presente" (tuvo al menos una factura), aunque la suma diera 0.
  const sumaProvMes = new Map();
  const acumular = (provId, mesRef, monto) => {
    if (!provId || !mesRef) return;
    const key = `${provId}-${mesRef}`;
    sumaProvMes.set(key, (sumaProvMes.get(key) || 0) + monto);
  };

  if (provIds.length > 0) {
    const [siiVentanaRes, extVentanaRes] = await Promise.all([
      client
        .from("facturas_sii_recibidas")
        .select("proveedor_id, monto_total, fecha_emision, tipo_contable")
        .eq("pais", pais)
        .in("proveedor_id", provIds)
        .gte("fecha_emision", desdeFecha),
      client
        .from("facturas_externas")
        .select("proveedor_id, monto_total, moneda, fecha_emision, periodo_mes")
        .eq("pais", pais)
        .in("proveedor_id", provIds)
        .neq("estado", "anulada")
        .gte("fecha_emision", desdeFecha),
    ]);

    for (const f of siiVentanaRes.data || []) {
      // SII siempre en moneda local; solo aplica si el proveedor opera en esa moneda.
      if (monedaDefaultPorProv.get(f.proveedor_id) !== monedaLocal) continue;
      if (!f.fecha_emision) continue;
      // Un CapEx (tipo_contable='activo') NO es un gasto recurrente: contamina el promedio y ya
      // proyecta por su lado en "Por pagar" (sus cuotas). Ej. Entel: la compra de teléfonos inflaba
      // el recurrente de ~$104k a $793k. Se excluye del promedio. (facturas_externas no tiene
      // tipo_contable → no hay path externa→activo, no requiere el mismo filtro.)
      if (f.tipo_contable === "activo") continue;
      acumular(f.proveedor_id, f.fecha_emision.slice(0, 7), Number(f.monto_total || 0));
    }
    for (const f of extVentanaRes.data || []) {
      if ((f.moneda || monedaLocal) !== monedaDefaultPorProv.get(f.proveedor_id)) continue;
      const mesRef = f.periodo_mes ? f.periodo_mes.slice(0, 7) : f.fecha_emision ? f.fecha_emision.slice(0, 7) : null;
      acumular(f.proveedor_id, mesRef, Number(f.monto_total || 0));
    }
  }

  // Promedio por proveedor sobre los meses PRESENTES de la ventana (divisor = cantidad de meses con
  // factura). Map proveedor_id → monto promedio (2 decimales). Sin meses presentes → no entra al map
  // y el consumidor cae a monto_recurrente.
  const montoPromedioPorProv = new Map();
  for (const prov of proveedores || []) {
    let suma = 0;
    let presentes = 0;
    for (const wm of ventanaMeses) {
      const key = `${prov.id}-${wm}`;
      if (sumaProvMes.has(key)) {
        suma += sumaProvMes.get(key);
        presentes += 1;
      }
    }
    if (presentes > 0) montoPromedioPorProv.set(prov.id, Math.round((suma / presentes) * 100) / 100);
  }

  const hoy = new Date().toISOString().split("T")[0];
  const hoyDate = new Date();
  // Generar entries hasta el 31/dic del año siguiente para alimentar la vista completa
  // (sin filtro temporal) del panel de Movimientos Futuros.
  const finAnio = new Date(hoyDate.getFullYear() + 1, 11, 31);
  const entries = [];

  // OnSign se detecta por ID, no por nombre (regla 10.7). IDs reales en DB: Chile 18, Perú 56.
  const ONSIGN_ID = pais === "Perú" ? 56 : 18;
  const onsignProv = (proveedores || []).find((p) => p.id === ONSIGN_ID);

  // Regla 10.163 (solo Chile): el rango arranca en 2026-01 para poder mostrar recurrentes cerrados
  // impagos (rojo). Perú mantiene el arranque en el mes actual (comportamiento previo).
  const esChile = pais === "Chile";
  const meses = [];
  const cursor = esChile ? new Date(2026, 0, 1) : new Date(hoyDate.getFullYear(), hoyDate.getMonth(), 1);
  while (cursor <= finAnio) {
    meses.push(new Date(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const pantallasXMes = {};
  if (onsignProv) {
    for (const mes of meses) {
      const ultimoDia = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
      const fechaStr = ultimoDia.toISOString().split("T")[0];
      // eslint-disable-next-line no-await-in-loop
      pantallasXMes[fechaStr] = await contarPantallasActivas(pais, fechaStr);
    }
  }

  // Conciliación de pago (regla 10.163, solo Chile): mapa proveedor_id → Set de meses "YYYY-MM"
  // saldados por un pago_proveedor en movimientos_caja (match por entidad_id, regla 8), con
  // tolerancia de straddle (el pago de un mes puede caer en los primeros 15 días del mes siguiente).
  // Claiming greedy: cada pago concilia UN solo mes (evita que un pago tape dos meses).
  const mesesKeys = meses.map((d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  const straddleHasta = (mesKey) => {
    const [y, mm] = mesKey.split("-").map(Number);
    const ny = mm === 12 ? y + 1 : y;
    const nm = mm === 12 ? 1 : mm + 1;
    return `${ny}-${String(nm).padStart(2, "0")}-15`;
  };
  const mesesPagadosPorProv = new Map();
  if (esChile) {
    const pagosPorProv = new Map();
    for (const m of movimientosCaja || []) {
      if (m.entidad_tipo !== "proveedor" || m.categoria_pago !== "pago_proveedor" || !m.entidad_id || !m.fecha) {
        continue;
      }
      const arr = pagosPorProv.get(m.entidad_id) || [];
      arr.push(m.fecha.slice(0, 10));
      pagosPorProv.set(m.entidad_id, arr);
    }
    for (const [provId, fechas] of pagosPorProv) {
      const ordenadas = [...fechas].sort();
      const claimed = new Set();
      const pagadas = new Set();
      for (const mesKey of mesesKeys) {
        const lo = `${mesKey}-01`;
        const hi = straddleHasta(mesKey);
        for (let i = 0; i < ordenadas.length; i += 1) {
          if (claimed.has(i)) continue;
          if (ordenadas[i] >= lo && ordenadas[i] <= hi) {
            claimed.add(i);
            pagadas.add(mesKey);
            break;
          }
        }
      }
      mesesPagadosPorProv.set(provId, pagadas);
    }
  }

  // Primer mes de actividad por proveedor (factura o pago más antiguo). No se proyecta el recurrente
  // antes de ese mes → evita falsos vencidos de proveedores de alta reciente (ej. Clay, onboarded en
  // abril, no debe mostrar rojo en ene/feb/mar). Solo Chile.
  const primerMesPorProv = new Map();
  if (esChile) {
    const registrar = (provId, mesKey) => {
      const actual = primerMesPorProv.get(provId);
      if (!actual || mesKey < actual) primerMesPorProv.set(provId, mesKey);
    };
    // Facturas: mesProveedorConFactura tiene keys `${provId}-YYYY-MM`.
    for (const key of mesProveedorConFactura) {
      const mesKey = key.slice(-7);
      const provId = Number(key.slice(0, key.length - 8));
      if (provId) registrar(provId, mesKey);
    }
    // Pagos.
    for (const m of movimientosCaja || []) {
      if (m.entidad_tipo === "proveedor" && m.categoria_pago === "pago_proveedor" && m.entidad_id && m.fecha) {
        registrar(m.entidad_id, m.fecha.slice(0, 7));
      }
    }
  }

  for (const prov of proveedores || []) {
    const esOnSign = prov.id === ONSIGN_ID;
    const esMensual = prov.periodicidad === "mensual";

    // Label de la línea principal en el flujo: "{alias} — {concepto_flujo}" (alias primero).
    // Sin concepto_flujo → solo el alias; sin alias → razón social (prov.nombre). Regla 10.7: es
    // display, no matching. `nombreFlujo` (alias||nombre) se reusa también en los costos asociados.
    const nombreFlujo = getNombreProveedor(prov);
    const conceptoFlujo = prov.concepto_flujo?.trim();
    const labelPrincipal = conceptoFlujo ? `${nombreFlujo} — ${conceptoFlujo}` : nombreFlujo;

    for (const mes of meses) {
      const diaPago = prov.dia_pago || 30;
      const fechaPagoDate = new Date(mes.getFullYear(), mes.getMonth(), diaPago);

      if (fechaPagoDate.getMonth() !== mes.getMonth()) {
        fechaPagoDate.setDate(0);
      }

      const fechaPago = fechaPagoDate.toISOString().split("T")[0];
      const mesKey = `${mes.getFullYear()}-${String(mes.getMonth() + 1).padStart(2, "0")}`;

      // Corte 2026-01: no proyectar antes (saldado por saldo inicial).
      if (mesKey < "2026-01") continue;

      // Ventana de contrato (backward-compatible): si el proveedor tiene fechas de inicio/fin de
      // contrato, no se proyecta fuera de esa ventana (comparación por mes YYYY-MM, ambos extremos
      // inclusive). NULL en cualquiera de las dos = sin límite por ese lado (comportamiento previo).
      // Ej. arriendo desde 2026-08-11 hasta 2027-08-11 → proyecta solo ago-2026..ago-2027.
      if (prov.recurrente_desde && mesKey < prov.recurrente_desde.slice(0, 7)) continue;
      if (prov.recurrente_hasta && mesKey > prov.recurrente_hasta.slice(0, 7)) continue;

      // Cuotas adelantadas (línea principal, SOLO con contrato): las primeras N cuotas ya pagadas
      // por adelantado no se proyectan → la línea principal arranca `cuotas_adelantadas` meses
      // después del inicio del contrato. Ej. inicio ago-2026 + 1 adelantada → arranca sep-2026.
      // Requiere recurrente_desde como ancla. Sin contrato o N=0 → sin efecto (backward-compatible).
      if (prov.recurrente_con_contrato && prov.recurrente_desde) {
        const adelantadas = Number(prov.cuotas_adelantadas || 0);
        if (adelantadas > 0 && mesKey < shiftMesYYYYMM(prov.recurrente_desde, adelantadas)) continue;
      }

      // Anti alta-reciente (solo Chile): no proyectar antes del primer mes con actividad real del
      // proveedor (factura o pago) → evita falsos vencidos de proveedores nuevos.
      if (esChile && esMensual) {
        const primerMes = primerMesPorProv.get(prov.id);
        if (primerMes && mesKey < primerMes) continue;
      }

      // Dedup: si ya hay factura (SII/externa) del proveedor en ese mes, la fila la muestra "Por pagar".
      if (mesProveedorConFactura.has(`${prov.id}-${mesKey}`)) continue;

      if (esChile && esMensual) {
        // Regla 10.163: el recurrente sale de la proyección SOLO si está saldado (pago_proveedor
        // conciliado al mes). No saldado → se genera; si la fecha ya pasó, el render lo pinta rojo
        // (vencido impago). NO se descarta por fecha.
        if (mesesPagadosPorProv.get(prov.id)?.has(mesKey)) continue;
      } else if (fechaPago <= hoy) {
        // Perú y no-mensuales: comportamiento previo (solo futuro, sin marcar vencidos).
        continue;
      }

      // Mes de renovación/no-cobro (ej. HDI: julio). mes_sin_cobro es humano 1-12; mes.getMonth()
      // es 0-indexado → comparar (mes.getMonth() + 1). Solo salta si el proveedor lo tiene seteado.
      // No pisa la dedup por factura real (línea de más arriba): esto solo mata la fila fantasma.
      if (prov.mes_sin_cobro != null && mes.getMonth() + 1 === prov.mes_sin_cobro) {
        continue;
      }

      let monto;
      let monedaEntry;

      if (esOnSign) {
        const ultimoDia = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).toISOString().split("T")[0];
        const pantallas = pantallasXMes[ultimoDia] || 0;
        monto = Math.round(pantallas * ONSIGN_PRECIO_POR_PANTALLA * 100) / 100;
        monedaEntry = prov.moneda_default || prov.moneda_recurrente || monedaLocal;
      } else {
        // P6 (Fase 1) — prioridad del monto: (1) promedio de la ventana si hubo facturas → (2)
        // monto_recurrente como fallback → (3) skip si ambos 0/null (no proyecta basura). La moneda ya
        // NO se deriva de una factura: es la moneda operativa del proveedor (moneda_default).
        const promedio = montoPromedioPorProv.get(prov.id);
        monto = promedio != null ? promedio : Number(prov.monto_recurrente || 0);
        monedaEntry = prov.moneda_default || prov.moneda_recurrente || monedaLocal;
      }

      if (monto <= 0) continue;

      const ultimoDiaKey = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).toISOString().split("T")[0];

      // Override de fecha por ocurrencia (Fase 2): si hay una fila en pago_recurrente_fecha para este
      // proveedor+mes, su fecha_override PISA la fecha derivada de dia_pago. La identidad de la
      // ocurrencia sigue siendo el MES (el id es mensual, estable), SOLO cambia la fecha proyectada.
      // El monto NO se recalcula por la fecha nueva: la entry queda en su moneda origen (UF/CLP/…) y
      // la conversión downstream (FlujoCajaV2.convertir → getUFValue) ya clampea a la última UF/TC
      // conocida para fechas futuras, así que mover la fecha entre dos días futuros no altera el CLP.
      // Sin override → fechaFinal === fechaPago (dormante). Los guards de skip de arriba usan la
      // fecha original (identidad por mes): el override cambia el display, no qué ocurrencias emiten.
      // Línea principal → discriminador "main" (costo_asociado_id NULL en la tabla).
      const fechaOverride = overridesFechaPorOcurrencia.get(
        `${prov.id}-${mes.getFullYear()}-${mes.getMonth() + 1}-main`
      );
      const fechaFinal = fechaOverride || fechaPago;

      entries.push({
        id: `recurrente-${prov.id}-${mesKey}`,
        fecha_display: fechaFinal,
        importeEnMoneda: -monto,
        moneda_recurrente: monedaEntry,
        razon_social_receptor: esOnSign
          ? `${labelPrincipal} (${pantallasXMes[ultimoDiaKey] || 0} licencias)`
          : labelPrincipal,
        tipo: "proveedor",
        es_recurrente: true,
        // Campos para el override de fecha por ocurrencia (Fase 3, UI del lápiz). La clave de
        // escritura en pago_recurrente_fecha es (proveedor_id, periodo_año, periodo_mes). `fecha_default`
        // es la fecha DERIVADA de dia_pago (la "original", para mostrar y para el botón "restaurar");
        // `tiene_override` refleja si esta ocurrencia ya tiene una fila de override.
        proveedor_id: prov.id,
        periodo_año: mes.getFullYear(),
        periodo_mes: mes.getMonth() + 1,
        fecha_default: fechaPago,
        tiene_override: !!fechaOverride,
        costo_asociado_id: null, // línea principal (discriminador "main")
      });
    }
  }

  // ── Costos asociados (Fase 2) ──────────────────────────────────────────────────────────────
  // Cada fila de proveedor_costos_asociados genera su propia serie recurrente mensual dentro de la
  // ventana del contrato del proveedor [recurrente_desde .. recurrente_hasta] (inclusive), SIN
  // cuotas_adelantadas (arrancan en el inicio). Se muestran en el mismo panel "Recurrentes" como
  // egresos, con label distinguible de la línea principal ("<concepto> — <proveedor>"). La moneda
  // es la del costo (CLP/UF/USD/PEN) → se reexpresa a local por fecha en FlujoCajaV2 (aDisplay →
  // convertir, regla 10.185). Comparación por proveedor_id numérico (regla 10.7).
  // NOTA: los costos aún no tienen conciliación de pago propia; para Chile se muestran también los
  // meses ya vencidos dentro de la ventana (el render los pinta rojo, igual que la línea principal).
  // Perú: solo futuro. Sin dedup por factura ni mes_sin_cobro (conceptos separados de la línea).
  for (const costo of costosAsociados) {
    const prov = provPorId.get(costo.proveedor_id);
    if (!prov) continue;
    const montoCosto = Number(costo.monto || 0);
    if (montoCosto <= 0) continue;
    const monedaCosto = costo.moneda || monedaLocal;

    // Costo ÚNICO: UNA sola entrada en su propia `fecha` (pago one-time del contrato: garantía,
    // comisión, etc.). No entra al loop mensual ni se acota por la ventana del contrato — su fecha
    // manda (se reexpresa a local por esa fecha en FlujoCajaV2, 10.185). Se muestra hasta que se
    // pague (todavía sin conciliación propia, igual que los mensuales). Solo el corte 2026-01 aplica.
    if (costo.tipo === "unico") {
      const fechaUnico = costo.fecha ? costo.fecha.slice(0, 10) : null;
      if (!fechaUnico || fechaUnico < "2026-01-01") continue;
      entries.push({
        id: `recurrente-costo-${costo.id}`,
        fecha_display: fechaUnico,
        importeEnMoneda: -montoCosto,
        moneda_recurrente: monedaCosto,
        razon_social_receptor: `${getNombreProveedor(prov)} — ${(costo.concepto || "Costo").trim()}`,
        tipo: "proveedor",
        es_recurrente: true,
      });
      continue;
    }

    const diaPagoCosto = costo.dia_pago || 30;

    for (const mes of meses) {
      const fechaPagoDate = new Date(mes.getFullYear(), mes.getMonth(), diaPagoCosto);
      if (fechaPagoDate.getMonth() !== mes.getMonth()) fechaPagoDate.setDate(0);
      const fechaPago = fechaPagoDate.toISOString().split("T")[0];
      const mesKey = `${mes.getFullYear()}-${String(mes.getMonth() + 1).padStart(2, "0")}`;

      if (mesKey < "2026-01") continue;
      if (prov.recurrente_desde && mesKey < prov.recurrente_desde.slice(0, 7)) continue;
      if (prov.recurrente_hasta && mesKey > prov.recurrente_hasta.slice(0, 7)) continue;
      // Perú y no-Chile: solo futuro (sin marcar vencidos), como el resto de recurrentes de ese país.
      if (!esChile && fechaPago <= hoy) continue;

      // Override de fecha de ESTE costo asociado en ese mes → discriminador = costo.id (independiente
      // de la línea principal). Sin override → fechaFinal === fechaPago. El monto no se recalcula.
      const fechaOverrideCosto = overridesFechaPorOcurrencia.get(
        `${costo.proveedor_id}-${mes.getFullYear()}-${mes.getMonth() + 1}-${costo.id}`
      );
      const fechaFinalCosto = fechaOverrideCosto || fechaPago;

      entries.push({
        // id por MES (estable bajo override, misma razón que la línea principal): antes usaba fechaPago.
        id: `recurrente-costo-${costo.id}-${mesKey}`,
        fecha_display: fechaFinalCosto,
        importeEnMoneda: -montoCosto,
        moneda_recurrente: monedaCosto,
        razon_social_receptor: `${getNombreProveedor(prov)} — ${(costo.concepto || "Costo").trim()}`,
        tipo: "proveedor",
        es_recurrente: true,
        // Campos para el lápiz de override (Fase 3). costo_asociado_id = costo.id → discrimina esta
        // ocurrencia de la línea principal del mismo proveedor/mes.
        proveedor_id: costo.proveedor_id,
        periodo_año: mes.getFullYear(),
        periodo_mes: mes.getMonth() + 1,
        fecha_default: fechaPago,
        tiene_override: !!fechaOverrideCosto,
        costo_asociado_id: costo.id,
      });
    }
  }

  return entries.sort((a, b) => a.fecha_display.localeCompare(b.fecha_display));
};

// ─── Override de fecha por ocurrencia (pago_recurrente_fecha) — ESCRITURA (Fase 3) ─────────────
// Espejo de setFechaPagoSueldo/eliminarFechaPagoSueldo (pagosEmpleados.js). Solo mueve la FECHA
// proyectada de una ocurrencia recurrente; el monto NO se toca (sigue en su moneda origen, la
// conversión ya clampea a la última UF/TC conocida). 10.177: propaga el error, no lo traga a null.

// Upsert del override vía RPC upsert_pago_recurrente_fecha, que ramifica por costo_asociado_id y
// apunta al ÍNDICE PARCIAL correcto (supabase-js .upsert no sabe expresar el ON CONFLICT … WHERE de
// un índice parcial). costoAsociadoId: null = línea principal; = costo.id = ese costo asociado.
// pais Chile/Perú (10.10/10.53). createdBy = usuario real (10.104), nunca placeholder.
export const setFechaPagoRecurrente = async (proveedorId, año, mes, fecha, pais, createdBy, costoAsociadoId = null) => {
  const { data, error } = await client.rpc("upsert_pago_recurrente_fecha", {
    p_proveedor_id: proveedorId,
    p_periodo_anio: Number(año),
    p_periodo_mes: Number(mes),
    p_fecha_override: fecha,
    p_pais: pais,
    p_created_by: createdBy,
    p_costo_asociado_id: costoAsociadoId,
  });
  if (error) throw error;
  return data;
};

// Borra el override de ESA ocurrencia → la fila vuelve a la fecha derivada de dia_pago. El
// discriminador evita borrar la del otro (línea principal vs costo del mismo proveedor/mes).
export const eliminarFechaPagoRecurrente = async (proveedorId, año, mes, costoAsociadoId = null) => {
  let q = client
    .from("pago_recurrente_fecha")
    .delete()
    .eq("proveedor_id", proveedorId)
    .eq("periodo_año", Number(año))
    .eq("periodo_mes", Number(mes));
  // NULL en Postgres → .is(null); no-NULL → .eq(id). No mezclar ambas ocurrencias.
  q = costoAsociadoId == null ? q.is("costo_asociado_id", null) : q.eq("costo_asociado_id", costoAsociadoId);
  const { error } = await q;
  if (error) throw error;
};
